import React from 'react';
import { ActivityIndicator, Platform, Pressable, Text, View } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AuthProvider, BIOMETRIC_KEY, useAuth } from './src/AuthContext';
import { hasPin } from './src/security';
import { PinDots, PinPad, usePinShake } from './src/pin-pad';
import { Toaster } from './src/toast';
import SecuritySetupScreen from './src/screens/SecuritySetupScreen';
import { navigationRef } from './src/drawer';
import { BottomNav } from './src/bottom-nav';
import { NotificationBell } from './src/notification-bell';
import { PhotoViewerHost } from './src/photo-viewer';
import { COLORS } from './src/ui';
import MoreScreen from './src/screens/MoreScreen';
import { WebSidebar } from './src/web-sidebar';
import { useWide } from './src/layout';

import LoginScreen from './src/screens/LoginScreen';
import OtpScreen from './src/screens/OtpScreen';
import ForgotPasswordScreen from './src/screens/ForgotPasswordScreen';
import RegisterScreen from './src/screens/RegisterScreen';
import DashboardScreen from './src/screens/landlord/DashboardScreen';
import OwnersScreen from './src/screens/landlord/OwnersScreen';
import ArchivedApartmentsScreen from './src/screens/landlord/ArchivedApartmentsScreen';
import AssignOwnerScreen from './src/screens/landlord/AssignOwnerScreen';
import PastTenantsScreen from './src/screens/landlord/PastTenantsScreen';
import ApartmentsScreen from './src/screens/landlord/ApartmentsScreen';
import ApartmentDetailScreen from './src/screens/landlord/ApartmentDetailScreen';
import ApartmentStatsScreen from './src/screens/landlord/ApartmentStatsScreen';
import AddApartmentScreen from './src/screens/landlord/AddApartmentScreen';
import AddTenantScreen from './src/screens/landlord/AddTenantScreen';
import AddBillScreen from './src/screens/landlord/AddBillScreen';
import AddUtilityAccountScreen from './src/screens/landlord/AddUtilityAccountScreen';
import CreateHandoverScreen from './src/screens/landlord/CreateHandoverScreen';
import PaymentMethodScreen from './src/screens/landlord/PaymentMethodScreen';
import IssuesScreen from './src/screens/IssuesScreen';
import TenantHomeScreen from './src/screens/tenant/TenantHomeScreen';
import NewIssueScreen from './src/screens/tenant/NewIssueScreen';
import NotificationsScreen from './src/screens/NotificationsScreen';
import DocumentsScreen from './src/screens/DocumentsScreen';
import ProfileScreen from './src/screens/ProfileScreen';
import CompleteNameScreen from './src/screens/CompleteNameScreen';
import SupportScreen from './src/screens/SupportScreen';
import OwnerApartmentsScreen from './src/screens/owner/OwnerApartmentsScreen';
import OwnerApartmentDetailScreen from './src/screens/owner/OwnerApartmentDetailScreen';
import HandymanRequestsScreen from './src/screens/handyman/HandymanRequestsScreen';
import HandymenPickScreen from './src/screens/landlord/HandymenPickScreen';
import HoldPaymentScreen from './src/screens/landlord/HoldPaymentScreen';
import ListingsScreen from './src/screens/ListingsScreen';
import CreateListingScreen from './src/screens/landlord/CreateListingScreen';
import ConversationsScreen from './src/screens/ConversationsScreen';
import ChatScreen from './src/screens/ChatScreen';
import ReviewScreen from './src/screens/ReviewScreen';
import SubscriptionScreen from './src/screens/landlord/SubscriptionScreen';
import ReputationScreen from './src/screens/ReputationScreen';
import { initAnalytics } from './src/analytics';
import { captureSource } from './src/source';
import { initLocale, t, useLocale } from './src/i18n';
import { ErrorBoundary } from './src/ErrorBoundary';
import InvoicesScreen from './src/screens/InvoicesScreen';
import IssueInvoiceScreen from './src/screens/landlord/IssueInvoiceScreen';
import ContractScreen from './src/screens/landlord/ContractScreen';
import CorrectInvoiceScreen from './src/screens/landlord/CorrectInvoiceScreen';
import CreatorHomeScreen from './src/screens/creator/CreatorHomeScreen';
import CreatorInvitesScreen from './src/screens/creator/CreatorInvitesScreen';
import CreatorMoneyScreen from './src/screens/creator/CreatorMoneyScreen';
import ResidentHomeScreen from './src/screens/resident/ResidentHomeScreen';
import ResidentsScreen from './src/screens/landlord/ResidentsScreen';

const Stack = createNativeStackNavigator();

const screenOptions = {
  headerStyle: { backgroundColor: COLORS.card },
  headerTintColor: COLORS.text,
};

// Влезлите потребители виждат камбанка за известия горе-вдясно на всеки екран.
const appScreenOptions = { ...screenOptions, headerRight: () => <NotificationBell /> };

// Кореновите екрани на табовете нямат стрелка за връщане — движението е през
// долната лента. Второстепенните екрани (от „Още") си запазват стрелката.
const tabRoot = { headerBackVisible: false };

// Заключен екран: лице/отпечатък (ако е включено) ИЛИ ПИН-ът от първия вход.
// ПИН-ът е с истинска клавиатура на екрана (точки + кръгли бутони), не
// системната — както в банково приложение.
function LockScreen() {
  const { user, unlock, unlockWithPin, signOut } = useAuth();
  const [pin, setPin] = React.useState('');
  const [bioEnabled, setBioEnabled] = React.useState(false);
  const [pinSet, setPinSet] = React.useState(false);
  const { offset, shake } = usePinShake();

  React.useEffect(() => {
    (async () => {
      const bio = (await AsyncStorage.getItem(BIOMETRIC_KEY)) === '1';
      setBioEnabled(bio);
      setPinSet(await hasPin());
      // при включена биометрия питаме веднага — един поглед и е вътре
      if (bio) void unlock();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryPin = async (value: string) => {
    setPin(value);
    if (value.length !== 4) return;
    if (!(await unlockWithPin(value))) {
      shake();
      setPin('');
    }
  };

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg, padding: 24 }}>
      <Text style={{ fontSize: 40 }}>🔒</Text>
      <Text style={{ fontSize: 21, fontWeight: '700', color: COLORS.text, marginTop: 10 }}>
        {user ? t('Здравейте, {name}', { name: user.firstName }) : t('Здравейте')}
      </Text>
      <Text style={{ color: COLORS.muted, marginTop: 2 }}>
        {pinSet ? t('Въведете ПИН, за да продължите') : t('Отключете, за да продължите')}
      </Text>

      {pinSet && (
        <>
          <PinDots value={pin} offset={offset} />
          <PinPad value={pin} onChange={tryPin} />
        </>
      )}

      {bioEnabled && (
        <Pressable onPress={unlock} style={{ marginTop: 22, padding: 8 }} hitSlop={8}>
          <Text style={{ color: COLORS.primary, fontWeight: '700', fontSize: 16 }}>
            {t('Отключи с лице / отпечатък')}
          </Text>
        </Pressable>
      )}

      <Pressable onPress={signOut} style={{ marginTop: 14 }} hitSlop={8}>
        <Text style={{ color: COLORS.muted }}>{t('Изход от акаунта')}</Text>
      </Pressable>
    </View>
  );
}

// Админ/модератор няма какво да прави в приложението — само изход.
function AdminNotSupportedScreen() {
  const { signOut } = useAuth();
  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg, padding: 24 }}>
      <Text style={{ fontSize: 44 }}>🛠️</Text>
      <Text style={{ fontSize: 20, fontWeight: '700', color: COLORS.text, marginTop: 12, textAlign: 'center' }}>
        {t('Този акаунт е за админ панела')}
      </Text>
      <Text style={{ color: COLORS.muted, textAlign: 'center', marginTop: 6, maxWidth: 340 }}>
        {t('Влезте на admin.livupp.com. Приложението е за наемодатели, пропърти мениджъри, наематели, собственици, майстори и криейтъри.')}
      </Text>
      <Pressable
        onPress={signOut}
        style={{ backgroundColor: COLORS.primary, borderRadius: 10, paddingVertical: 14, paddingHorizontal: 40, marginTop: 20 }}
      >
        <Text style={{ color: '#fff', fontWeight: '600', fontSize: 16 }}>{t('Изход')}</Text>
      </Pressable>
    </View>
  );
}

function Navigator() {
  const { user, loading, locked, needsSecuritySetup } = useAuth();

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg }}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  if (user && locked) return <LockScreen />;
  // Първи вход на устройството → ПИН + биометрия, преди същинския екран
  if (user && needsSecuritySetup) return <SecuritySetupScreen />;

  // key: при вход/изход/смяна на роля стекът се създава начисто —
  // иначе React Navigation запазва съвпадащи по име екрани (напр. „Репутация“)
  // и след „Изход“ оставаш на тях вместо на входа.
  const navKey = user ? `${user.role}:${user.id}` : 'auth';

  // Сайтът праща линкове с hash към app.livupp.com:
  //   #register       → отваря направо екрана за регистрация
  //   #review-tenant  → кампания „отзиви за наематели“: невлязъл → регистрация,
  //                     влязъл наемодател/ПМ → направо екрана „Минали наематели“
  // Facebook/Instagram понякога лепят ?fbclid=… СЛЕД фрагмента („#check?fbclid=…“) —
  // затова гледаме само частта преди „?“/„&“.
  const rawHash =
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.hash : '';
  const hash = rawHash.split('?')[0].split('&')[0];
  //   #check          → реклама „провери наемателя“: невлязъл → направо в
  //                     публичната проверка по телефон (регистрацията идва после)
  const wantsReviewTenant = hash === '#review-tenant';
  const wantsRegister = hash === '#register' || wantsReviewTenant;
  const wantsCheck = hash === '#check' || hash === '#proveri';

  if (!user) {
    // Екран, отворен директно от линк (#register, #check), няма история →
    // стрелката води към „Вход“, за да не остане човекът в задънена улица.
    const authScreenOptions = ({ navigation }: any) => ({
      ...appScreenOptions,
      headerBackVisible: false,
      headerLeft: () => (
        <Pressable
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Login'))}
          hitSlop={10}
          style={{ paddingRight: 12, paddingVertical: 4 }}
        >
          <Text style={{ color: COLORS.primary, fontSize: 16, fontWeight: '700' }}>{t('‹ Вход')}</Text>
        </Pressable>
      ),
    });
    return (
      <Stack.Navigator
        key={navKey}
        screenOptions={authScreenOptions}
        initialRouteName={wantsRegister ? 'Register' : wantsCheck ? 'PublicReputation' : 'Login'}
      >
        <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
        <Stack.Screen name="Otp" component={OtpScreen} options={{ title: t('SMS код') }} />
        <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} options={{ title: t('Забравена парола') }} />
        <Stack.Screen name="Register" component={RegisterScreen} options={{ title: t('Регистрация') }} />
        <Stack.Screen name="PublicReputation" component={ReputationScreen} options={{ title: t('Провери наемател') }} />
      </Stack.Navigator>
    );
  }

  // Админ и модератор работят в админ панела — приложението няма техни
  // екрани и всяка заявка им връща „няма права". Без този изход човек
  // остава заклещен без никакво меню.
  if (user.role === 'ADMIN' || user.role === 'MODERATOR') {
    return <AdminNotSupportedScreen />;
  }

  if (user.role === 'LANDLORD' || user.role === 'AGENCY') {
    return (
      <Stack.Navigator
        key={navKey}
        screenOptions={appScreenOptions}
        initialRouteName={!user.firstName ? 'CompleteName' : wantsReviewTenant ? 'PastTenants' : undefined}
      >
        <Stack.Screen name="Dashboard" component={DashboardScreen} options={{ title: t('Начало'), ...tabRoot }} />
        <Stack.Screen name="CompleteName" component={CompleteNameScreen} options={{ title: t('Вашето име'), headerShown: false }} />
        <Stack.Screen name="Apartments" component={ApartmentsScreen} options={{ title: t('Имоти'), ...tabRoot }} />
        <Stack.Screen name="ApartmentDetail" component={ApartmentDetailScreen} options={{ title: t('Имот') }} />
        <Stack.Screen name="ApartmentStats" component={ApartmentStatsScreen} options={{ title: t('Статистика') }} />
        <Stack.Screen name="Owners" component={OwnersScreen} options={{ title: t('Собственици') }} />
        <Stack.Screen name="ArchivedApartments" component={ArchivedApartmentsScreen} options={{ title: t('Архивирани') }} />
        <Stack.Screen name="AssignOwner" component={AssignOwnerScreen} options={{ title: t('Собственик') }} />
        <Stack.Screen name="PastTenants" component={PastTenantsScreen} options={{ title: t('Минали наематели') }} />
        <Stack.Screen name="Residents" component={ResidentsScreen} options={{ title: t('Обитатели') }} />
        <Stack.Screen name="AddApartment" component={AddApartmentScreen} options={{ title: t('Нов имот') }} />
        <Stack.Screen name="AddTenant" component={AddTenantScreen} options={{ title: t('Нов наемател') }} />
        <Stack.Screen name="AddBill" component={AddBillScreen} options={{ title: t('Нова сметка') }} />
        <Stack.Screen name="AddUtilityAccount" component={AddUtilityAccountScreen} options={{ title: t('ИТН номер') }} />
        <Stack.Screen name="CreateHandover" component={CreateHandoverScreen} options={{ title: t('Протокол') }} />
        <Stack.Screen name="PaymentMethod" component={PaymentMethodScreen} options={{ title: t('Получаване на наема') }} />
        <Stack.Screen name="Issues" component={IssuesScreen} options={{ title: t('Нередности') }} />
        <Stack.Screen name="NewIssue" component={NewIssueScreen} options={{ title: t('Ремонт / нередност') }} />
        <Stack.Screen name="HandymenPick" component={HandymenPickScreen} options={{ title: t('Майстори') }} />
        <Stack.Screen name="HoldPayment" component={HoldPaymentScreen} options={{ title: t('Задържане') }} />
        <Stack.Screen name="Documents" component={DocumentsScreen} options={{ title: t('Документи') }} />
        <Stack.Screen name="Invoices" component={InvoicesScreen} options={{ title: t('Фактури') }} />
        <Stack.Screen name="IssueInvoice" component={IssueInvoiceScreen} options={{ title: t('Нова фактура') }} />
        <Stack.Screen name="Contract" component={ContractScreen} options={{ title: t('Договор за наем') }} />
        <Stack.Screen name="CorrectInvoice" component={CorrectInvoiceScreen} options={{ title: t('Корекция на фактура') }} />
        <Stack.Screen name="Listings" component={ListingsScreen} options={{ title: t('Свободни апартаменти') }} />
        <Stack.Screen name="CreateListing" component={CreateListingScreen} options={{ title: t('Нова обява') }} />
        <Stack.Screen name="Conversations" component={ConversationsScreen} options={{ title: t('Съобщения'), ...tabRoot }} />
        <Stack.Screen name="Chat" component={ChatScreen} options={{ title: t('Разговор') }} />
        <Stack.Screen name="Review" component={ReviewScreen} options={{ title: t('Оценка') }} />
        <Stack.Screen name="Subscription" component={SubscriptionScreen} options={{ title: t('Абонамент') }} />
        <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия') }} />
        <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил') }} />
        <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
        <Stack.Screen name="Reputation" component={ReputationScreen} options={{ title: t('Провери наемател') }} />
        <Stack.Screen name="More" component={MoreScreen} options={{ title: t('Още'), ...tabRoot }} />
      </Stack.Navigator>
    );
  }

  if (user.role === 'TENANT') {
    return (
      <Stack.Navigator key={navKey} screenOptions={appScreenOptions}>
        <Stack.Screen name="TenantHome" component={TenantHomeScreen} options={{ title: t('Моят дом'), ...tabRoot }} />
        <Stack.Screen name="Issues" component={IssuesScreen} options={{ title: t('Ремонти'), ...tabRoot }} />
        <Stack.Screen name="NewIssue" component={NewIssueScreen} options={{ title: t('Нова нередност') }} />
        <Stack.Screen name="Documents" component={DocumentsScreen} options={{ title: t('Документи') }} />
        <Stack.Screen name="Invoices" component={InvoicesScreen} options={{ title: t('Фактури') }} />
        <Stack.Screen name="Listings" component={ListingsScreen} options={{ title: t('Свободни апартаменти') }} />
        <Stack.Screen name="Conversations" component={ConversationsScreen} options={{ title: t('Съобщения'), ...tabRoot }} />
        <Stack.Screen name="Chat" component={ChatScreen} options={{ title: t('Разговор') }} />
        <Stack.Screen name="Review" component={ReviewScreen} options={{ title: t('Оценка') }} />
        <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия') }} />
        <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил') }} />
        <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
        <Stack.Screen name="Reputation" component={ReputationScreen} options={{ title: t('Провери наемател') }} />
        <Stack.Screen name="More" component={MoreScreen} options={{ title: t('Още'), ...tabRoot }} />
      </Stack.Navigator>
    );
  }

  if (user.role === 'OWNER') {
    return (
      <Stack.Navigator key={navKey} screenOptions={appScreenOptions}>
        <Stack.Screen name="OwnerApartments" component={OwnerApartmentsScreen} options={{ title: t('Моите имоти'), ...tabRoot }} />
        <Stack.Screen name="OwnerApartmentDetail" component={OwnerApartmentDetailScreen} options={{ title: t('Имот') }} />
        <Stack.Screen name="Issues" component={IssuesScreen} options={{ title: t('Ремонти'), ...tabRoot }} />
        <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия') }} />
        <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил'), ...tabRoot }} />
        <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
        <Stack.Screen name="Reputation" component={ReputationScreen} options={{ title: t('Провери наемател') }} />
      </Stack.Navigator>
    );
  }

  // Обитател: живее в имота, но не е страна по наема. Вижда само адреса и
  // нередностите — без наем, плащания, документи, договор и чат.
  if (user.role === 'RESIDENT') {
    return (
      <Stack.Navigator key={navKey} screenOptions={appScreenOptions}>
        <Stack.Screen name="ResidentHome" component={ResidentHomeScreen} options={{ title: t('Моят дом'), ...tabRoot }} />
        <Stack.Screen name="NewIssue" component={NewIssueScreen} options={{ title: t('Нова нередност') }} />
        <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия'), ...tabRoot }} />
        <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил'), ...tabRoot }} />
        <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
      </Stack.Navigator>
    );
  }

  if (user.role === 'CREATOR') {
    return (
      <Stack.Navigator key={navKey} screenOptions={appScreenOptions}>
        <Stack.Screen name="CreatorHome" component={CreatorHomeScreen} options={{ title: t('Криейтър'), ...tabRoot }} />
        <Stack.Screen name="CreatorMoney" component={CreatorMoneyScreen} options={{ title: t('Пари'), ...tabRoot }} />
        <Stack.Screen name="CreatorInvites" component={CreatorInvitesScreen} options={{ title: t('Покани'), ...tabRoot }} />
        <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия') }} />
        <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил'), ...tabRoot }} />
        <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
      </Stack.Navigator>
    );
  }

  // HANDYMAN
  return (
    <Stack.Navigator key={navKey} screenOptions={appScreenOptions}>
      <Stack.Screen
        name="HandymanRequests"
        component={HandymanRequestsScreen}
        options={{ title: t('Поръчки'), ...tabRoot }}
      />
      <Stack.Screen name="Notifications" component={NotificationsScreen} options={{ title: t('Известия'), ...tabRoot }} />
      <Stack.Screen name="Profile" component={ProfileScreen} options={{ title: t('Профил'), ...tabRoot }} />
      <Stack.Screen name="Support" component={SupportScreen} options={{ title: t('Поддръжка') }} />
        <Stack.Screen name="Reputation" component={ReputationScreen} options={{ title: t('Провери наемател') }} />
    </Stack.Navigator>
  );
}

// Долната лента се показва само при влязъл, отключен потребител —
// и само на тесен екран (на широк уеб я замества страничната навигация)
function BottomNavHost() {
  const { user, locked, needsSecuritySetup } = useAuth();
  const wide = useWide();
  if (!user || locked || needsSecuritySetup || wide) return null;
  return <BottomNav role={user.role} />;
}

// Странична навигация — само широк уеб, при влязъл и отключен потребител
function WebSidebarHost() {
  const { user, locked, needsSecuritySetup } = useAuth();
  const wide = useWide();
  if (!user || locked || needsSecuritySetup || !wide) return null;
  return <WebSidebar />;
}

initAnalytics();
void captureSource();

export default function App() {
  // Езикът се определя преди първия екран (запазен избор / телефон / часова
  // зона / IP). При смяна от Профил → Език цялото дърво се монтира наново
  // (key={locale}), за да се преведе всеки екран.
  const [localeReady, setLocaleReady] = React.useState(false);
  const locale = useLocale();
  React.useEffect(() => {
    initLocale().finally(() => setLocaleReady(true));
  }, []);
  if (!localeReady) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.bg }}>
        <ActivityIndicator color={COLORS.primary} />
      </View>
    );
  }
  return (
    <ErrorBoundary key={locale}>
    <SafeAreaProvider>
      <AuthProvider>
        <NavigationContainer ref={navigationRef}>
          <StatusBar style="dark" />
          <View style={{ flex: 1 }}>
            <View style={{ flex: 1, flexDirection: 'row' }}>
              <WebSidebarHost />
              <View style={{ flex: 1 }}>
                <Navigator />
              </View>
            </View>
            <BottomNavHost />
          </View>
          <PhotoViewerHost />
          <Toaster />
        </NavigationContainer>
      </AuthProvider>
    </SafeAreaProvider>
    </ErrorBoundary>
  );
}
